import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { blink } from '@/lib/blink'
import { liveOAuthTestService, LiveTestResult, TokenValidation, RateLimitInfo } from '@/services/liveOAuthTestService'
import { toast } from 'sonner'
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Mail,
  Calendar,
  Search,
  Send,
  Trash2,
  Loader2,
  RefreshCw,
  Shield,
  TrendingUp,
  XCircle,
  Zap,
  BarChart3,
  AlertTriangle
} from 'lucide-react'

export default function LiveOAuthTestDashboard() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [testResults, setTestResults] = useState<LiveTestResult[]>([])
  const [selectedProvider, setSelectedProvider] = useState<'gmail' | 'outlook'>('outlook')
  const [tokenValidation, setTokenValidation] = useState<TokenValidation | null>(null)
  const [rateLimits, setRateLimits] = useState<RateLimitInfo[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  
  // Test inputs
  const [testEmail, setTestEmail] = useState('')
  const [testSubject, setTestSubject] = useState('Live OAuth API Test')
  const [testBody, setTestBody] = useState('This is a live test email sent via OAuth-authenticated API.')
  const [searchQuery, setSearchQuery] = useState('inbox')
  const [eventTitle, setEventTitle] = useState('Test Calendar Event')
  const [eventStart, setEventStart] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 16))
  const [eventEnd, setEventEnd] = useState(new Date(Date.now() + 90000000).toISOString().slice(0, 16))

  // Load user and initial state
  useEffect(() => {
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      setUser(state.user)
      if (state.user) {
        // Refresh data when user changes
        const results = liveOAuthTestService.getTestResults({ limit: 50 })
        setTestResults(results)
        
        const stats = liveOAuthTestService.getStatistics()
        setStatistics(stats)
        
        const limits = liveOAuthTestService.getRateLimitStatus(selectedProvider)
        setRateLimits(limits)
      }
    })
    return unsubscribe
  }, [selectedProvider])

  const refreshData = () => {
    const results = liveOAuthTestService.getTestResults({ limit: 50 })
    setTestResults(results)
    
    const stats = liveOAuthTestService.getStatistics()
    setStatistics(stats)
    
    const limits = liveOAuthTestService.getRateLimitStatus(selectedProvider)
    setRateLimits(limits)
  }

  const validateToken = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const validation = await liveOAuthTestService.validateToken(user.id, selectedProvider)
      setTokenValidation(validation)
      
      if (validation.isValid) {
        toast.success(`${selectedProvider} token is valid`)
      } else {
        toast.error(`Invalid token: ${validation.error}`)
      }
    } catch (error) {
      toast.error('Token validation failed')
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const runComprehensiveTest = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const results = await liveOAuthTestService.runComprehensiveTest(
        user.id,
        selectedProvider,
        {
          testEmail: testEmail || undefined,
          testSubject,
          testBody,
          skipSend: !testEmail
        }
      )
      
      toast.success(`Test completed: ${results.filter(r => r.status === 'success').length}/${results.length} passed`)
    } catch (error: any) {
      toast.error(`Test failed: ${error.message}`)
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const testSendEmail = async () => {
    if (!user || !testEmail) {
      toast.error('Please enter a recipient email')
      return
    }
    
    setLoading(true)
    try {
      let result: LiveTestResult
      
      if (selectedProvider === 'gmail') {
        result = await liveOAuthTestService.testGmailSendEmail(user.id, testEmail, testSubject, testBody)
      } else {
        result = await liveOAuthTestService.testOutlookSendEmail(user.id, testEmail, testSubject, testBody)
      }
      
      if (result.status === 'success') {
        toast.success('Email sent successfully')
      } else if (result.status === 'rate_limited') {
        toast.error(`Rate limited: ${result.error}`)
      } else {
        toast.error(`Send failed: ${result.error}`)
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const testListMessages = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      let result: LiveTestResult
      
      if (selectedProvider === 'gmail') {
        result = await liveOAuthTestService.testGmailListMessages(user.id, 10)
      } else {
        result = await liveOAuthTestService.testOutlookListMessages(user.id, 10)
      }
      
      if (result.status === 'success') {
        toast.success(`Found messages: ${result.response?.value?.length || result.response?.messages?.length || 0}`)
      } else {
        toast.error(`List failed: ${result.error}`)
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const testSearchMessages = async () => {
    if (!user) return
    
    setLoading(true)
    try {
      let result: LiveTestResult
      
      if (selectedProvider === 'gmail') {
        result = await liveOAuthTestService.testGmailSearchMessages(user.id, searchQuery)
      } else {
        result = await liveOAuthTestService.testOutlookSearchMessages(user.id, searchQuery)
      }
      
      if (result.status === 'success') {
        toast.success(`Search completed: ${result.response?.value?.length || result.response?.messages?.length || 0} results`)
      } else {
        toast.error(`Search failed: ${result.error}`)
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const testCreateCalendarEvent = async () => {
    if (!user || selectedProvider !== 'outlook') return
    
    setLoading(true)
    try {
      const attendees = testEmail ? [testEmail] : []
      const result = await liveOAuthTestService.testOutlookCreateCalendarEvent(
        user.id,
        eventTitle,
        eventStart,
        eventEnd,
        attendees
      )
      
      if (result.status === 'success') {
        toast.success('Calendar event created')
      } else {
        toast.error(`Create failed: ${result.error}`)
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const testListCalendarEvents = async () => {
    if (!user || selectedProvider !== 'outlook') return
    
    setLoading(true)
    try {
      const result = await liveOAuthTestService.testOutlookListCalendarEvents(user.id, 10)
      
      if (result.status === 'success') {
        toast.success(`Found ${result.response?.value?.length || 0} upcoming events`)
      } else {
        toast.error(`List failed: ${result.error}`)
      }
    } catch (error: any) {
      toast.error(`Error: ${error.message}`)
    } finally {
      setLoading(false)
      refreshData()
    }
  }

  const clearResults = () => {
    liveOAuthTestService.clearResults()
    setTestResults([])
    setRateLimits([])
    setStatistics(null)
    setTokenValidation(null)
    toast.info('Results cleared')
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Sign in to test OAuth API integration</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => blink.auth.login()} className="w-full">
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="h-8 w-8" />
            Live OAuth Test Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Validate real OAuth tokens and test API calls with rate limit monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refreshData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={clearResults} variant="outline" size="sm">
            <Trash2 className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      {/* Provider Selection & Token Validation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedProvider} onValueChange={(v) => setSelectedProvider(v as 'gmail' | 'outlook')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="gmail">Gmail</TabsTrigger>
                <TabsTrigger value="outlook">Outlook</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Token Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={validateToken} disabled={loading} className="w-full" size="sm">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
              Validate Token
            </Button>
            {tokenValidation && (
              <div className="flex items-center gap-2">
                {tokenValidation.isValid ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">Valid</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">Invalid</span>
                  </>
                )}
              </div>
            )}
            {tokenValidation?.email && (
              <p className="text-xs text-muted-foreground">{tokenValidation.email}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={runComprehensiveTest} disabled={loading} className="w-full" size="sm">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Run Full Test Suite
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tests</p>
                  <p className="text-2xl font-bold">{statistics.total}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Success</p>
                  <p className="text-2xl font-bold text-green-600">{statistics.success}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Errors</p>
                  <p className="text-2xl font-bold text-red-600">{statistics.error}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rate Limited</p>
                  <p className="text-2xl font-bold text-yellow-600">{statistics.rateLimited}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Duration</p>
                  <p className="text-2xl font-bold">{Math.round(statistics.avgDuration)}ms</p>
                </div>
                <Clock className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rate Limits */}
      {rateLimits.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Rate Limit Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {rateLimits.map((limit, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{limit.endpoint.split('/').pop()}</span>
                    <span className="text-muted-foreground">
                      {limit.remaining} / {limit.limit} remaining
                    </span>
                  </div>
                  <Progress value={(limit.remaining / limit.limit) * 100} />
                  <p className="text-xs text-muted-foreground">
                    Resets at {limit.reset.toLocaleTimeString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Operations */}
      <Tabs defaultValue="email" className="space-y-4">
        <TabsList>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-2" />
            Email Tests
          </TabsTrigger>
          {selectedProvider === 'outlook' && (
            <TabsTrigger value="calendar">
              <Calendar className="h-4 w-4 mr-2" />
              Calendar Tests
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="email" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Send Email */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Send Email
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>To</Label>
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="recipient@example.com"
                  />
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input
                    value={testSubject}
                    onChange={(e) => setTestSubject(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Body</Label>
                  <Input
                    value={testBody}
                    onChange={(e) => setTestBody(e.target.value)}
                  />
                </div>
                <Button onClick={testSendEmail} disabled={loading} className="w-full" size="sm">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Test Send
                </Button>
              </CardContent>
            </Card>

            {/* List Messages */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  List Messages
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Retrieve the 10 most recent messages from your inbox
                </p>
                <Button onClick={testListMessages} disabled={loading} className="w-full" size="sm">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Test List
                </Button>
              </CardContent>
            </Card>

            {/* Search Messages */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search Messages
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Search Query</Label>
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="inbox, from:sender, subject:topic"
                  />
                </div>
                <Button onClick={testSearchMessages} disabled={loading} className="w-full" size="sm">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Test Search
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {selectedProvider === 'outlook' && (
          <TabsContent value="calendar" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Create Event */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Create Event
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={eventTitle}
                      onChange={(e) => setEventTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Start</Label>
                    <Input
                      type="datetime-local"
                      value={eventStart}
                      onChange={(e) => setEventStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End</Label>
                    <Input
                      type="datetime-local"
                      value={eventEnd}
                      onChange={(e) => setEventEnd(e.target.value)}
                    />
                  </div>
                  <Button onClick={testCreateCalendarEvent} disabled={loading} className="w-full" size="sm">
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
                    Test Create
                  </Button>
                </CardContent>
              </Card>

              {/* List Events */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    List Events
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Retrieve upcoming events for the next 7 days
                  </p>
                  <Button onClick={testListCalendarEvents} disabled={loading} className="w-full" size="sm">
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
                    Test List
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Test Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Live Test Results ({testResults.length})
          </CardTitle>
          <CardDescription>
            Real-time logs of all API operations with OAuth tokens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <div className="space-y-3">
              {testResults.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tests run yet. Click any test button above to begin.</p>
                </div>
              ) : (
                testResults.map((result) => (
                  <Card key={result.id} className="border-l-4" style={{
                    borderLeftColor: result.status === 'success' ? '#22c55e' : result.status === 'rate_limited' ? '#f59e0b' : '#ef4444'
                  }}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {result.status === 'success' && <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />}
                          {result.status === 'error' && <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />}
                          {result.status === 'rate_limited' && <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />}
                          <div>
                            <p className="font-semibold">{result.operation}</p>
                            <p className="text-xs text-muted-foreground">{result.provider.toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={result.status === 'success' ? 'default' : result.status === 'rate_limited' ? 'secondary' : 'destructive'}>
                            {result.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground">{result.duration}ms</span>
                        </div>
                      </div>

                      <Separator className="my-3" />

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Timestamp:</span>
                          <span>{result.timestamp.toLocaleTimeString()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Method:</span>
                          <Badge variant="outline">{result.method}</Badge>
                        </div>
                        {result.statusCode && (
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Status Code:</span>
                            <Badge variant={result.statusCode < 300 ? 'default' : 'destructive'}>
                              {result.statusCode}
                            </Badge>
                          </div>
                        )}

                        {result.rateLimit && (
                          <div className="mt-3 p-3 bg-muted rounded-lg space-y-2">
                            <p className="font-medium text-xs">Rate Limit Info</p>
                            <div className="flex items-center justify-between text-xs">
                              <span>Remaining:</span>
                              <span>{result.rateLimit.remaining} / {result.rateLimit.limit}</span>
                            </div>
                            <Progress value={(result.rateLimit.remaining / result.rateLimit.limit) * 100} className="h-1" />
                          </div>
                        )}

                        {result.error && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="font-medium text-xs text-red-600 mb-1">Error</p>
                            <p className="text-xs text-red-600">{result.error}</p>
                          </div>
                        )}

                        {result.response && result.status === 'success' && (
                          <details className="mt-3">
                            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                              View Response
                            </summary>
                            <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">
                              {JSON.stringify(result.response, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
